import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getVirtualMachines,
  setShowMoreInfoForVmTemplate,
  setShowRemoteAccessCountExceededModal,
  virtualMachinesSelectors,
} from '../../../../../../../redux/configuration/virtual-machine-entities/slice';
import { RootState } from '../../../../../../../redux/store';
import {
  useGetAllQuery,
  useGetVMTemplatesInventoryQuery,
} from '../../../../../../../redux/configuration/virtual-machine-entities/api';
import { TableLoader } from '../../../../../../../components/Table/TableLoader';
import { prepTemplateVmsForTable } from '../../../../components/VirtualMachineEntities/Add/components/TemplateTabContent/utils';
import { calculateTopologyRemoteAccessCount } from '../../../../components/VirtualMachineEntities/utils';
import { currentUserSelectors } from '../../../../../../../redux/configuration/currentUser/slice';
import {
  determineIsNotPermittedToSeeDisabledTemplates,
  determineShowDisabledTemplates,
} from './utils';
import { TemplatesTabContent } from '.';

function TemplatesTabContentContainer({
  setShowMoreInfoForVmTemplate,
  setShowRemoteAccessCountExceededModal,
  isDiagramView,
  showDisabledTemplates,
}: VMTemplatesTabContentContainerProps): ReactElement {
  const { uid: topologyUid } = useParams<EditTopologyParams>();
  const { isFetching, isLoading, transformedTemplateVms } =
    useGetVMTemplatesInventoryQuery(topologyUid, {
      selectFromResult: ({ data, isLoading, isFetching }) => ({
        data,
        isFetching,
        isLoading,
        transformedTemplateVms: data
          ? prepTemplateVmsForTable(data, showDisabledTemplates)
          : undefined,
      }),
    });

  const {
    topologyRemoteAccessCount,
    isError: isErrorAllVms,
    isLoading: isLoadingAllVms,
    isFetching: isFetchingAllVms,
  } = useGetAllQuery(topologyUid, {
    selectFromResult: ({ data, isFetching, isLoading, isError }) => ({
      isError,
      isFetching,
      isLoading,
      topologyRemoteAccessCount: calculateTopologyRemoteAccessCount(data),
    }),
  });

  if (
    isFetching ||
    isLoading ||
    !transformedTemplateVms ||
    isLoadingAllVms ||
    isFetchingAllVms ||
    isErrorAllVms
  ) {
    return <TableLoader />;
  }

  return (
    <TemplatesTabContent
      isDiagramView={isDiagramView}
      templateVms={transformedTemplateVms || []}
      isFetching={isFetching || isLoading}
      setShowMoreInfoForVmTemplate={setShowMoreInfoForVmTemplate}
      topologyRemoteAccessCount={topologyRemoteAccessCount}
      setShowRemoteAccessCountExceededModal={
        setShowRemoteAccessCountExceededModal
      }
    />
  );
}

type ConnectedVMTemplatesTabContentOwnProps = {
  isDiagramView?: boolean;
};

export const mapStateToProps = (
  state: RootState,
  ownProps: ConnectedVMTemplatesTabContentOwnProps,
) => {
  const roles = currentUserSelectors.getUserRolesArray(state);
  const isNotPermitted = determineIsNotPermittedToSeeDisabledTemplates(roles);
  const showDisabledTemplatesFromState =
    virtualMachinesSelectors.getShowDisabledTemplates(state);
  const showDisabledTemplates = determineShowDisabledTemplates({
    isNotPermitted,
    showDisabledTemplatesFromState,
  });

  return {
    isDiagramView: ownProps.isDiagramView,
    showDisabledTemplates,
    showMoreInfoForVmTemplate:
      getVirtualMachines(state).showMoreInfoForVmTemplate,
  };
};

const mapDispatchToProps = {
  setShowMoreInfoForVmTemplate,
  setShowRemoteAccessCountExceededModal,
};

type VMTemplatesTabContentContainerProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

export const ConnectedVMTemplatesTabContent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TemplatesTabContentContainer);
